/**
 * Módulo NPS — pesquisa de satisfação por módulo para profissionais e gestores.
 *
 * COMO ENTRA NA APLICAÇÃO SEM TOCAR NO SHELL
 * `registerModule` (src/modules/index.js) chama cada módulo com
 * `{ app: Vue, store, router }`. Este usa os três e não precisa de UMA LINHA em
 * `main.vue`, `global-modals.vue`, `src/constants/` ou `src/services/`:
 *   - `router.afterEach` alimenta o gatilho (nunca `beforeEach`, para não
 *     entrar no caminho crítico da navegação);
 *   - o card é montado programaticamente num `<div>` anexado ao body, só na
 *     primeira vez que fica elegível — antes disso não existe nada no DOM;
 *   - `store` fornece o usuário e os parâmetros do escritório.
 *
 * ESPERA O USUÁRIO RESOLVER
 * O registro dos módulos acontece no bootstrap, ANTES de o usuário ser
 * carregado (o `beforeEach` do router é que chama `getCurrentUser`). Nesse
 * instante `IS_INTERNAL` e `features_access` ainda não existem — e ler cedo
 * demais mostraria o card para a AAWZ. Por isso o serviço só arma depois que o
 * store resolve, no mesmo padrão de `services/boot-splash.js`.
 */
import apiNps from '@modules/nps/api/apiNps'
import { GlobalBus } from '@services/bus'
import { TELEMETRIA } from '@constants/Text'
import {
  MODO_PERMANENCIA,
  SINAIS_DO_BUS,
  chaveDoEvento,
} from '@modules/nps/constants/eventos'
import {
  criarGatilho,
  GATILHO_FORCADO,
} from '@modules/nps/services/nps-trigger'
import { destruirCard, montarCard } from '@modules/nps/services/nps-card-host'
import { ehInterno, idDoProfissional } from '@modules/nps/services/nps-usuario'
import { registrarInterceptor } from '@modules/nps/services/nps-rede'

function versaoDoBuild() {
  try {
    return require('@/versao.json') || {}
  } catch (error_) {
    // `versao.json` só existe depois do build.sh; em dev o arquivo pode faltar,
    // e a resposta continua valendo — só perde o carimbo de versão.
    // eslint-disable-next-line no-console
    console.warn('[nps] versao.json ausente:', error_ && error_.message)
    return {}
  }
}

function dispositivoAtual(largura) {
  if (largura < 600) return 'mobile'
  if (largura < 960) return 'tablet'
  return 'desktop'
}

/**
 * Contexto que permite REPRODUZIR a tela que gerou a nota: versão do frontend
 * (com branch, commit e build) e as feature flags do profissional e do
 * escritório. Sem isso a nota é um número solto — dois profissionais na mesma
 * versão veem plataformas diferentes conforme as flags.
 */
function primeiroPreenchido(valores) {
  return valores.find((valor) => !!valor) || ''
}

/**
 * Versão do frontend em quatro pedaços. O `versao.json` do build.sh usa
 * `current_branch`/`last_commit`/`build_time`; o versionado no repo usa
 * `feature`/`commit`/`data`. Os dois formatos convivem — em dev o arquivo do
 * repo é o que existe.
 */
function versaoParaGravar() {
  const versao = versaoDoBuild()
  return {
    versao_frontend: primeiroPreenchido([
      versao.versao,
      globalThis.__VERSION__,
    ]),
    branch_frontend: primeiroPreenchido([
      versao.current_branch,
      versao.feature,
    ]),
    commit_frontend: primeiroPreenchido([versao.last_commit, versao.commit]),
    build_frontend: primeiroPreenchido([versao.build_time, versao.data]),
  }
}

function contextoDaResposta(router) {
  const parametros = globalThis.PARAMETROS || {}
  const escritorio = parametros.escritorio || {}
  const rotaAtual = (router && router.currentRoute) || {}
  const largura = globalThis.innerWidth

  return {
    ...versaoParaGravar(),
    features_access: parametros.features_access || {},
    office_features: parametros.office_features_access || {},
    rota: rotaAtual.name || '',
    dispositivo: dispositivoAtual(largura),
    largura_viewport: largura,
    id_escritorio: escritorio.id_escritorio || '',
  }
}

function abrirCard(Vue, store, router, dadosDaPesquisa) {
  const usuario = store.getters['user/currentUser'] || {}
  // `import()` dinâmico: o card aparece no máximo ~4 vezes por ano por pessoa —
  // não há por que pesar no bundle inicial de quem talvez nunca o veja.
  return import(
    /* webpackChunkName: "nps-card" */ '@modules/nps/components/nps-card/nps-card.vue'
  )
    .then((modulo) => {
      montarCard(Vue, {
        store: store,
        router: router,
        componente: modulo.default,
        props: {
          modulo: dadosDaPesquisa.modulo,
          pergunta: dadosDaPesquisa.pergunta,
          rodape: dadosDaPesquisa.rodape,
          contexto: contextoDaResposta(router),
          idProfissional: idDoProfissional(usuario),
          // O que trouxe o card: 'evento' (tarefa concluída) ou 'permanencia'.
          // O card manda o mesmo valor para a telemetria e para o servidor
          // (que ignora a chave até ter coluna — quando a migration entrar,
          // passa a gravar sem mudar nada aqui).
          gatilho: dadosDaPesquisa.gatilho || MODO_PERMANENCIA,
        },
        aoFechar: destruirCard,
      })
    })
    .catch((error_) => {
      // "Loading chunk failed" acontece de verdade: aba antiga aberta depois de
      // um deploy novo. Sem este catch vira unhandled rejection. O gatilho já
      // marcou `disparou`, então a pesquisa se perde NESTA rota — a próxima
      // navegação chama `reiniciar()`, que zera a marca e tenta de novo.
      // eslint-disable-next-line no-console
      console.warn(
        '[nps] chunk do card não carregou:',
        error_ && error_.message
      )
    })
}

/**
 * Helper de console para depurar a pesquisa fora de produção.
 *
 * Existe porque o card depende de quatro condições de tela MAIS a resposta do
 * servidor: quando ele não aparece, sem isto a única saída é adivinhar qual das
 * cinco falhou. `__nps.status()` mostra exatamente o que o servidor respondeu,
 * com o motivo.
 *
 * `PRODUCAO` é a mesma global que o resto da app usa (definida em
 * `apiUserConfig` a partir de `/api/config`).
 */
function exporHelperDeTeste(app, store, router, gatilho) {
  if (globalThis.PRODUCAO) return

  globalThis.__nps = {
    /** O que o servidor responde, com o motivo de não ser elegível. */
    status(modulo) {
      return apiNps.elegibilidade(modulo || 'crm', true).then(function (r) {
        // eslint-disable-next-line no-console
        console.log('[nps] elegibilidade:', r)
        return r
      })
    },

    /** Monta o card na marra, mesmo que o servidor diga que não é elegível. */
    abrir(modulo, origem) {
      const chave = modulo || 'crm'
      return apiNps.elegibilidade(chave, true).then(function (r) {
        abrirCard(app, store, router, {
          modulo: chave,
          pergunta: (r && r.pergunta) || 'Que nota você dá para ' + chave + '?',
          rodape: (r && r.rodape) || '',
          gatilho: origem || GATILHO_FORCADO,
        })
        return r
      })
    },

    /**
     * Simula uma ação concluída, como se o handler tivesse emitido o gtag.
     * Ex.: na rota de comissões, `__nps.evento('comissoes_fechamento_aprovar')`.
     * O card entra depois de ESPERA_POS_EVENTO_MS de tela sossegada.
     */
    evento(chave) {
      gatilho.aoEvento(chave)
      // eslint-disable-next-line no-console
      console.log('[nps] evento recebido:', chave, gatilho._estado())
      return gatilho._estado()
    },

    /** Modo da rota, o que armou o relógio, evento pendente — sem adivinhar. */
    estado() {
      const atual = gatilho._estado()
      // eslint-disable-next-line no-console
      console.log('[nps] estado:', atual)
      return atual
    },

    /** Por que o card não apareceu da última vez. */
    get ultimoMotivo() {
      return globalThis.__npsUltimoMotivo || null
    },

    /** Fecha e remove o card. */
    fechar: destruirCard,

    /** Apaga a trava local deste navegador (a do servidor continua valendo). */
    limparTrava() {
      const usuario = store.getters['user/currentUser'] || {}
      const id = idDoProfissional(usuario)
      Object.keys(globalThis.localStorage)
        .filter(function (chave) {
          return chave.indexOf('aawz_nps:') === 0
        })
        .forEach(function (chave) {
          globalThis.localStorage.removeItem(chave)
        })
      // eslint-disable-next-line no-console
      console.log('[nps] trava local limpa para o profissional', id)
    },

    /** Quem a sessão diz que é — o gate mais comum de não aparecer. */
    quemSouEu() {
      const usuario = store.getters['user/currentUser'] || {}
      const dados = {
        id: idDoProfissional(usuario),
        interno: ehInterno(usuario),
        tipo: usuario.tipo_usuario,
      }
      // eslint-disable-next-line no-console
      console.log('[nps] sessão:', dados)
      return dados
    },
  }
}

/**
 * Liga o gatilho aos sinais de "ação concluída".
 *
 * `TELEMETRIA.EVENTO` é o espelho de todo `$gtagEvent` (main.js); os
 * `SINAIS_DO_BUS` são conclusões que não passam pelo gtag. O try/catch é
 * redundante com o `invokeWithErrorHandling` do Vue 2.6, mas deixa explícito
 * o contrato: a pesquisa NUNCA pode quebrar o handler de negócio que emitiu.
 */
export function assinarEventosDeNegocio(gatilho) {
  const entregar = function (chave) {
    try {
      gatilho.aoEvento(chave)
    } catch (error_) {
      // eslint-disable-next-line no-console
      console.warn('[nps] evento ignorado:', error_ && error_.message)
    }
  }
  GlobalBus.$on(TELEMETRIA.EVENTO, function (evento) {
    entregar(chaveDoEvento(evento))
  })
  SINAIS_DO_BUS.forEach(function (sinal) {
    GlobalBus.$on(sinal, function () {
      entregar(sinal)
    })
  })
}

export function npsModule({ app, store, router }) {
  if (!store || !router || !app) return

  registrarInterceptor()

  const gatilho = criarGatilho({
    usuario: function () {
      return store.getters['user/currentUser']
    },
    aoFicarElegivel: function (dadosDaPesquisa) {
      abrirCard(app, store, router, dadosDaPesquisa)
    },
  })
  exporHelperDeTeste(app, store, router, gatilho)

  let pararDeObservar = null
  // `immediate: true` executa o callback ANTES de `pararDeObservar` receber a
  // função de unwatch. Se o usuário já estiver resolvido no boot (hidratação de
  // store, HMR, mudança futura na ordem de carga), o `if (pararDeObservar)`
  // seria null, o watcher nunca encerraria e cada troca de usuário registraria
  // outro `router.afterEach`. A flag torna o encerramento independente da ordem.
  let jaArmou = false
  const encerrarObservacao = () => {
    if (pararDeObservar) {
      pararDeObservar()
      pararDeObservar = null
    }
  }
  const quandoOUsuarioResolver = (usuario) => {
    if (jaArmou) {
      encerrarObservacao()
      return
    }
    if (!usuario || !idDoProfissional(usuario)) return

    // Usuário interno da AAWZ nunca vê: ela entra na plataforma pelo escritório
    // do cliente para dar suporte, e a nota seria gravada no escritório dele —
    // contaminando o corte por escritório que a tela do admin mostra.
    if (ehInterno(usuario)) {
      jaArmou = true
      encerrarObservacao()
      return
    }

    router.afterEach(function (para) {
      gatilho.aoNavegar(para && para.name)
    })
    assinarEventosDeNegocio(gatilho)
    gatilho.aoNavegar(router.currentRoute && router.currentRoute.name)

    jaArmou = true
    encerrarObservacao()
  }

  pararDeObservar = store.watch(
    () => store.getters['user/currentUser'],
    quandoOUsuarioResolver,
    { immediate: true }
  )
}

export default npsModule
